import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Menu, Search, ShoppingCart, UserRound, Wallet, X } from "lucide-react";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import useAssetReady from "../hooks/useAssetReady";
import useCart from "../hooks/useCart";
import SearchModal from "./SearchModal";
import CartDrawer from "./CartDrawer";
import ThemeToggle from "./ThemeToggle";
import LanguageSwitcher from "./LanguageSwitcher";

// The shop, day to day. Everything else (About, Products, Compliance, Locations, Stories, Press, Contact, policies)
// lives on the Profile page, reached from the profile icon at the top right.
const LINKS = [
  { to: "/", key: "nav.home" },
  { to: "/menu", key: "nav.menu" },
  { to: "/categories", key: "nav.categories" },
  { to: "/daily-catch", key: "nav.dailyCatch" },
  { to: "/orders", key: "nav.orders" },
] as const;

// Approximate widths matching each real link's rendered length, so the
// skeleton doesn't visibly reflow into the real content once ready.
const LINK_SKELETON_WIDTHS = ["w-10", "w-10", "w-16", "w-16", "w-16"];

/**
 * The navbar's text is always light (foam), designed to sit transparent over a page's DARK hero and to turn into a
 * dark bar once you scroll. A page that starts LIGHT (no dark hero) would leave white text on a pale background in
 * light mode, so these routes (and anything beneath them, e.g. /menu/<catch>) get the dark bar from the very top. Add a
 * prefix here whenever a page starts light.
 */
const SOLID_NAV_PREFIXES = ["/menu", "/categories", "/daily-catch", "/checkout", "/orders", "/profile", "/wallet", "/share-earn", "/favourites", "/refund-assistance", "/complaint", "/help", "/faq", "/policies", "/account/privacy", "/catch-pass", "/party", "/about-app", "/coming-soon"];

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const lastY = useRef(0);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const solidOnTop = SOLID_NAV_PREFIXES.some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`));
  const reducedMotion = usePrefersReducedMotion();
  const navReady = useAssetReady("/logo.png", 1200);
  const { itemCount } = useCart();

  useEffect(() => {
    // Seed from the real scroll position first so a page that loads already
    // scrolled (browser scroll restoration, a deep link) doesn't get compared
    // against a stale 0 baseline and hide the header with no scroll gesture.
    lastY.current = window.scrollY;
    setScrolled(window.scrollY > 24);

    const onScroll = () => {
      // Ignore scroll changes while the drawer is open — the CSS scroll lock
      // below blocks real wheel/touch gestures, but this also guards against
      // any programmatic scrollY change so `hidden` can never drift while
      // open and then snap the header away the instant it closes.
      if (open) return;
      const y = window.scrollY;
      setScrolled(y > 24);
      setHidden(y > lastY.current && y > 120);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k")) return;
      // Ctrl+K is a native text-editing shortcut (delete-to-end-of-line) in
      // many browsers/OSes when a field is focused — don't hijack it out
      // from under someone typing in the newsletter or contact form.
      const target = document.activeElement;
      const isEditable =
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (isEditable) return;
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    // Lock background scroll while the mobile drawer, search modal, or cart
    // drawer is open — otherwise the page keeps scrolling behind it,
    // `hidden` drifts silently, and the header snaps away the instant it
    // closes.
    document.body.style.overflow = open || searchOpen || cartOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, searchOpen, cartOpen]);

  const closeSearch = () => {
    setSearchOpen(false);
    // Deferred: focusing the trigger button synchronously while still
    // inside an Enter keydown's handling meant the same physical Enter
    // press also activated the now-focused button's own onClick (browsers
    // fire a button's "press" activation for a focused button on the
    // trailing key event) — reopening search immediately after this closed
    // it. A macrotask delay lets the current key event finish first.
    setTimeout(() => searchTriggerRef.current?.focus(), 0);
  };

  return (
    <motion.header
      animate={{ y: hidden && !open ? "-100%" : "0%" }}
      transition={{ duration: reducedMotion ? 0 : 0.3, ease: "easeInOut" }}
      // top-8, not top-0: sits below DisclaimerBanner's always-on h-8 strip.
      className={`fixed inset-x-0 top-8 z-50 transition-colors duration-300 ${
        scrolled || open || solidOnTop
          ? "bg-ocean-950/90 shadow-lg shadow-ocean-950/20 backdrop-blur-md"
          : "bg-transparent"
      }`}
    >
      {/* Compact-on-scroll (ZP-87): a transform-only scale, not a padding/
          height change, so the fixed header's own size shift never reflows
          page content — CLS only measures unexpected shifts of in-flow
          elements, and this header is `position: fixed`, but a transform
          keeps the animation itself GPU-compositable and jank-free either
          way, which a raw Tailwind class swap on padding would not be. */}
      <motion.nav
        animate={{ scale: scrolled ? 0.88 : 1 }}
        transition={{ duration: reducedMotion ? 0 : 0.3, ease: "easeInOut" }}
        style={{ transformOrigin: "top" }}
        className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4"
      >
        <NavLink to="/" className="flex shrink-0 items-center gap-2 font-display text-lg font-bold text-foam-50">
          <img src="/logo.png" alt="ZenFresh" className="h-10 w-10 object-contain" />
          <span className="hidden sm:inline">ZenFresh</span>
        </NavLink>

        {/* min-w-0 + overflow-x-auto: translated nav labels vary in rendered
            width per language (e.g. Malayalam runs noticeably wider than
            English) — without this, a long-enough label set can push the
            icon cluster past the viewport edge at the lg breakpoint instead
            of this row simply scrolling internally. shrink-0 on the logo
            and icon cluster (below) guarantees those stay fully reachable
            regardless of how wide the link row's content gets.
            Deliberately NOT flex-1: that would force this row to greedily
            consume 100% of justify-between's free space via flex-grow,
            leaving zero space left for justify-between to distribute —
            which collapses the gap between the logo and the first link to
            0. Default flex-shrink (1) + min-w-0 already lets this row
            shrink and scroll internally when content overflows; it just
            doesn't need to grow to do that. */}
        <div className="hidden min-w-0 items-center justify-center gap-6 overflow-x-auto [scrollbar-width:none] lg:flex lg:flex-1 xl:gap-8 [&::-webkit-scrollbar]:hidden">
          {!navReady &&
            LINK_SKELETON_WIDTHS.map((w, i) => (
              <div key={i} aria-hidden="true" className={`h-3.5 ${w} shrink-0 animate-pulse rounded bg-foam-50/20`} />
            ))}
          {navReady &&
            LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `shrink-0 whitespace-nowrap text-[13px] font-medium tracking-wide transition-colors xl:text-sm ${
                    isActive ? "text-teal-300" : "text-foam-100/80 hover:text-foam-50"
                  }`
                }
              >
                {t(link.key)}
              </NavLink>
            ))}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle />

          <button
            ref={searchTriggerRef}
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            className="flex items-center gap-1.5 rounded-full p-2 text-foam-50 transition-colors hover:bg-white/10 lg:px-3"
          >
            <Search className="h-5 w-5 lg:h-4 lg:w-4" />
          </button>

          <button
            type="button"
            onClick={() => setCartOpen(true)}
            aria-label={`Cart, ${itemCount} item${itemCount === 1 ? "" : "s"}`}
            className="relative flex items-center gap-1.5 rounded-full p-2 text-foam-50 transition-colors hover:bg-white/10 lg:px-3"
          >
            <ShoppingCart className="h-5 w-5 lg:h-4 lg:w-4" />
            {itemCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-coral-400 px-1 text-[10px] font-bold text-white">
                {itemCount}
              </span>
            )}
          </button>

          <NavLink
            to="/wallet"
            aria-label="Wallet"
            className={({ isActive }) =>
              `flex h-9 w-9 items-center justify-center rounded-full transition-colors ${isActive ? "bg-teal-400 text-ocean-950" : "text-foam-50 hover:bg-white/10"}`
            }
          >
            <Wallet className="h-5 w-5 lg:h-4 lg:w-4" aria-hidden="true" />
          </NavLink>

          <NavLink
            to="/profile"
            aria-label="Profile"
            className={({ isActive }) =>
              `flex h-9 w-9 items-center justify-center rounded-full transition-colors ${isActive ? "bg-teal-400 text-ocean-950" : "bg-white/10 text-foam-50 hover:bg-white/20"}`
            }
          >
            <UserRound className="h-5 w-5" aria-hidden="true" />
          </NavLink>

          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            className="text-foam-50 lg:hidden"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </motion.nav>

      <SearchModal open={searchOpen} onClose={closeSearch} />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: reducedMotion ? 0 : 0.25, ease: "easeInOut" }}
            className="overflow-hidden lg:hidden"
          >
            <div className="flex max-h-[70vh] flex-col gap-1 overflow-y-auto px-6 pb-6">
              {LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-base font-medium ${
                      isActive ? "bg-white/10 text-teal-300" : "text-foam-100/80"
                    }`
                  }
                >
                  {t(link.key)}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
